/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiFlexGroup,
  EuiHorizontalRule,
  EuiModal,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
} from "@inscopix/ideas-eui";
import { ModelSchema } from "api/client/types/DjangoClient.types";
import { WorkflowEditorTimeline } from "./WorkflowEditorForm/WorkflowEditorTimeline/WorkflowEditorTimeline";
import { WorkflowEditorProvider } from "./WorkflowEditorProvider/WorkflowEditorProvider";
import { WorkflowEditorPages } from "./WorkflowEditorForm/pages/WorkflowEditorPages";
import { WorkflowEditorBottomBar } from "./WorkflowEditorForm/WorkflowEditorBottomBar";
import { useEffect, useState } from "react";
import { StoreApi } from "zustand";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import assert from "assert";
import {
  createWorkflowEditorStore,
  WorkflowEditorStoreState,
} from "./WorkflowEditorProvider/WorkflowEditor.store";
import { DjangoClient } from "api/client/DjangoClient";
import { ModalLoading } from "components/ModalLoading/ModalLoading";
import { ModalError } from "components/ModalError/ModalError";

export interface ModalCreateWorkflowProps {
  analysisTableIds: ModelSchema["GdtAnalysisTableSchema"]["id"][];
  onClose: () => void;
}

export const ModalCreateWorkflow = ({
  analysisTableIds,
  onClose,
}: ModalCreateWorkflowProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const [store, setStore] = useState<StoreApi<WorkflowEditorStoreState>>();
  const tableIds = useDataTableContext((s) => {
    if (analysisTableIds === undefined) {
      return [];
    }
    const filteredTables = s.tables.filter((t) => {
      return (
        analysisTableIds.some((id) => id === t.id) && t.kind === "analysis"
      );
    });
    assert(
      filteredTables.length === analysisTableIds.length,
      "Missing tables from store in workflow provider",
    );
    return filteredTables.map(({ id }) => id).sort();
  });

  useEffect(() => {
    if (store === undefined && loading && error === undefined) {
      void DjangoClient.wrs.workflow.validateTables
        .queryFn({
          analysis_tables: tableIds.sort(),
        })
        .then((res) => {
          if (res.data !== undefined) {
            setStore(createWorkflowEditorStore({ data: res.data }));
          } else {
            setError(res.error);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [store, analysisTableIds, loading, error, tableIds]);

  if (store !== undefined) {
    return (
      <EuiModal
        onClose={onClose}
        css={css`
          width: 90vw;
          height: 90vh;
          max-width: 90vw;
          display: flex;
          flex-direction: column;
        `}
      >
        <WorkflowEditorProvider store={store}>
          <EuiModalHeader>
            <EuiFlexGroup direction="column" gutterSize="none">
              <EuiModalHeaderTitle component="h3">
                Create Workflow
              </EuiModalHeaderTitle>

              <WorkflowEditorTimeline />

              <EuiHorizontalRule margin="none" />
            </EuiFlexGroup>
          </EuiModalHeader>

          <WorkflowEditorPages />
          <EuiModalFooter>
            <WorkflowEditorBottomBar />
          </EuiModalFooter>
        </WorkflowEditorProvider>
      </EuiModal>
    );
  }
  if (loading) {
    return <ModalLoading onClose={onClose} />;
  }

  return (
    <ModalError
      onClose={onClose}
      title={"Create Workflow"}
      error={error ?? "Unknown Error"}
    />
  );
};
